import { Action, Location } from 'history';
import { useViewContext } from 'modules/messagingInsights/context';
import { useEffect } from 'react';
import { Prompt } from 'react-router';
import { useViewsModals } from './hooks/useViewsModals';
import { UnsavedViewModal } from './modals/UnsavedViewModal';
import { useGetUnsavedSettings } from './hooks/useGetUnsavedSettings';

export const UnsavedViewPrompt = () => {
  const modals = useViewsModals();
  const { viewId } = useViewContext();
  const unsavedSettings = useGetUnsavedSettings();

  /**
   * If a user has unsaved changes, block user from navigating to another route in the Iterable app.
   * A custom modal is displayed, checking if the user would like to save or discard changes.
   */
  const leavingPage = (nextLocation: Location, action: Action) => {
    const { pathname } = nextLocation;
    const state = nextLocation.state || {};
    const { isBlocked = true } = state as { isBlocked?: boolean };
    if (viewId && unsavedSettings) {
      if (!isBlocked && action === 'PUSH') {
        return true;
      }
      modals.unsaved.toggle(true, pathname);
      return false;
    }
    return true;
  };

  /**
   * We can't display the custom Unsaved View Modal when a user navigates away via the back/refresh buttons or by
   * closing the tab. In this case, we display the standard browser prompt checking if the user wants to stay on the page.
   */
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (viewId && unsavedSettings) {
        event.preventDefault();

        event.returnValue = '';
        return '';
      }
    };
    window.onbeforeunload = handleBeforeUnload;
    return () => {
      window.onbeforeunload = null;
    };
  }, [viewId, unsavedSettings]);

  return (
    <>
      <Prompt when message={leavingPage} />
      {modals.unsaved.isShown && <UnsavedViewModal />}
    </>
  );
};
